import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { compressImage } from "../../firebase/upload";
import { FiUpload, FiPlus, FiTrash2, FiEdit2, FiX, FiCheck } from "react-icons/fi";

const BookStore = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState({});
  const [form, setForm] = useState({ image: "", name: "", address: "", phone: "", url: "" });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const q = query(collection(db, "bookStore"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ image: "", name: "", address: "", phone: "", url: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const key = "bookstore-img";
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const base64 = await compressImage(file, 300, 0.4);
      setForm((f) => ({ ...f, image: base64 }));
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading((u) => ({ ...u, [key]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { alert("Name is required"); return; }
    try {
      if (editingId) {
        await updateDoc(doc(db, "bookStore", editingId), form);
      } else {
        await addDoc(collection(db, "bookStore"), { ...form, createdAt: Date.now() });
      }
      resetForm();
      fetchItems();
    } catch (e) { alert("Failed: " + e.message); }
  };

  const handleEdit = (item) => {
    setForm({ image: item.image || "", name: item.name || "", address: item.address || "", phone: item.phone || "", url: item.url || "" });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this book store entry?")) return;
    try {
      await deleteDoc(doc(db, "bookStore", id));
      fetchItems();
    } catch (e) { alert("Delete failed: " + e.message); }
  };

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Book Store</h1>
            <p className="text-sm text-gray-400">Manage book store listings</p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#e84fa5] text-white text-[12px] font-bold uppercase rounded-lg hover:bg-pink-600 transition">
            <FiPlus size={14} /> {showForm ? "Close" : "Add Book Store"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
            <h2 className="text-[14px] font-extrabold mb-5">{editingId ? "Edit" : "Add"} Book Store</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Image</label>
                {form.image && <img src={form.image} alt="" className="h-20 w-auto object-contain mb-2 bg-white rounded border p-1" />}
                <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
                  <FiUpload size={14} />
                  {uploading["bookstore-img"] ? "Uploading..." : "Upload Image"}
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading["bookstore-img"]} />
                </label>
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Name *</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Book store name" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">URL</label>
                <input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Phone No.</label>
                <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Phone number" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Address</label>
                <input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Address" />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <button type="submit"
                className="px-6 py-2.5 bg-[#e84fa5] text-white font-bold text-[12px] uppercase rounded-lg hover:bg-pink-600 transition">
                {editingId ? "Update" : "Save"}
              </button>
              <button type="button" onClick={resetForm}
                className="px-4 py-2.5 text-[12px] text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition font-semibold">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="bg-white rounded-xl border border-gray-100">
          {items.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-gray-400">No book store entries yet. Click "Add Book Store" to create one.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((item) => (
                <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition">
                  {item.image ? (
                    <img src={item.image} alt="" className="h-16 w-16 object-cover rounded-lg border shrink-0" />
                  ) : (
                    <div className="h-16 w-16 bg-gray-100 rounded-lg shrink-0 flex items-center justify-center text-[20px] text-gray-300"><FiBook /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[14px] font-bold text-black truncate">{item.name}</h3>
                    {item.address && <p className="text-[11px] text-gray-400 truncate">{item.address}</p>}
                    {item.phone && <p className="text-[11px] text-gray-400">{item.phone}</p>}
                    {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#e84fa5] hover:underline truncate block">{item.url}</a>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleEdit(item)} className="p-2 text-gray-400 hover:text-[#e84fa5] transition"><FiEdit2 size={14} /></button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-gray-400 hover:text-red-500 transition"><FiTrash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default BookStore;
